import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "../components/seo/SeoHead";
import { useTranslation } from "../i18n";
import { renderBrandText } from "../utils/brandText";
import "./BugDictionary.css";

const pageCopy = {
  es: {
    kicker: "TheQAHub Bug Dictionary",
    title: "El diccionario vivo de bugs para QA Engineers",
    intro:
      "No existe una lista cerrada de bugs, y justo por eso esta seccion puede convertirse en una referencia viva. Aqui ordenamos bugs reales por categoria, impacto, origen y detectabilidad para que aprender, reportar y debatir sea mas facil.",
    stats: [
      { value: "12", label: "categorias base" },
      { value: "18", label: "bugs documentados" },
      { value: "5", label: "subtipos TheQAHub" },
    ],
    guideTitle: "Como se lee este diccionario",
    guide: [
      {
        title: "Clasificacion multicapa",
        text: "Un mismo bug puede ser funcional, critico e intermitente a la vez. Por eso lo mostramos con varias capas, no con una etiqueta unica.",
      },
      {
        title: "Enfoque didactico",
        text: "Cada entrada explica que es, como suele aparecer, como reportarlo mejor y que pistas ayudan a detectarlo antes.",
      },
      {
        title: "Modelo vivo",
        text: "La estructura esta preparada para crecer con nuevas categorias, firmas de marca y sugerencias de la comunidad.",
      },
    ],
    searchLabel: "Buscar por nombre, categoria o sintoma",
    searchPlaceholder: "Ej: login, rendimiento, race condition...",
    filterCategory: "Categoria",
    filterImpact: "Impacto",
    allCategories: "Todas",
    allImpacts: "Todos",
    emptyTitle: "No hay coincidencias",
    emptyText: "Prueba con otra combinacion de filtros o con un termino mas general.",
    cardsTitle: "Bugs base documentados",
    cardsIntro:
      "Esta primera version mezcla clasificacion tecnica con lenguaje claro para que sirva tanto a testers junior como a equipos mas maduros.",
    signatureEyebrow: "Subtipos de bug marca de",
    signatureIntro:
      "Estos tipos de bugs los hemos creado nosotros para describir patrones de fallo reales con un lenguaje mas memorable, didactico y facil de compartir dentro de la comunidad.",
    expandTitle: "Haz crecer la clasificacion",
    expandText:
      "Si quieres proponer una nueva categoria, un bug recurrente o una firma TheQAHub, puedes mandarlo como sugerencia. Esta parte esta pensada para evolucionar hacia aportaciones de comunidad, comentarios o moderacion futura.",
    suggestionLabel: "Tu idea de bug o clasificacion",
    suggestionPlaceholder:
      "Ejemplo: Bug camaleón. Se reproduce solo cuando cambias de rol, idioma y viewport en la misma sesión...",
    sendMail: "Enviar sugerencia por email",
    contactCta: "Ir a contacto",
    mailSubject: "Sugerencia para TheQAHub Bug Dictionary",
    mailFallback: "Quiero proponer una nueva entrada para el Bug Dictionary de TheQAHub:",
    mailNote: "",
    fields: {
      origin: "Origen",
      detectability: "Detectabilidad",
      example: "Ejemplo",
      report: "Como reportarlo",
      detect: "Como detectarlo",
    },
    categories: ["Funcional", "UI", "UX", "Rendimiento", "Seguridad", "Compatibilidad", "Integracion", "API", "Logica", "Concurrencia", "Intermitente", "Datos", "Critico"],
    impacts: ["Critico", "Alto", "Medio", "Bajo"],
  },
  en: {
    kicker: "TheQAHub Bug Dictionary",
    title: "The living bug dictionary for QA Engineers",
    intro:
      "There is no closed list of bugs, and that is exactly what makes this section powerful. Here we organize real bugs by category, impact, origin and detectability so learning, reporting and discussing them becomes easier.",
    stats: [
      { value: "12", label: "core categories" },
      { value: "18", label: "documented bugs" },
      { value: "5", label: "TheQAHub signatures" },
    ],
    guideTitle: "How to read this dictionary",
    guide: [
      {
        title: "Multi-layer classification",
        text: "The same bug can be functional, critical and intermittent at the same time. That is why we show several layers instead of a single label.",
      },
      {
        title: "Didactic by design",
        text: "Each entry explains what it is, how it appears, how to report it better and which signals help detect it earlier.",
      },
      {
        title: "Built to evolve",
        text: "The structure is ready to grow with new categories, branded signatures and community suggestions.",
      },
    ],
    searchLabel: "Search by name, category or symptom",
    searchPlaceholder: "Ex: login, performance, race condition...",
    filterCategory: "Category",
    filterImpact: "Impact",
    allCategories: "All",
    allImpacts: "All",
    emptyTitle: "No matches found",
    emptyText: "Try another filter combination or a broader search term.",
    cardsTitle: "Documented core bugs",
    cardsIntro:
      "This first version mixes technical classification with plain language so it works for both junior testers and more mature teams.",
    signatureEyebrow: "Bug subtype signatures by",
    signatureIntro:
      "These names were created by us at </TheQAHub> to describe real failure patterns with language that is more memorable, didactic and easier to share across the community.",
    expandTitle: "Help expand the taxonomy",
    expandText:
      "If you want to propose a new category, a recurring bug or a TheQAHub signature, you can send it as a suggestion. This section is designed to evolve toward community contributions, comments or future moderation.",
    suggestionLabel: "Your bug or classification idea",
    suggestionPlaceholder:
      "Example: Chameleon bug. It only reproduces when you switch role, language and viewport in the same session...",
    sendMail: "Send suggestion by email",
    contactCta: "Go to contact",
    mailSubject: "Suggestion for TheQAHub Bug Dictionary",
    mailFallback: "I want to propose a new entry for TheQAHub Bug Dictionary:",
    mailNote: "",
    fields: {
      origin: "Origin",
      detectability: "Detectability",
      example: "Example",
      report: "How to report it",
      detect: "How to detect it",
    },
    categories: ["Functional", "UI", "UX", "Performance", "Security", "Compatibility", "Integration", "API", "Logic", "Concurrency", "Intermittent", "Data", "Critical"],
    impacts: ["Critical", "High", "Medium", "Low"],
  },
  de: {
    kicker: "TheQAHub Bug Dictionary",
    title: "Das lebendige Bug-Lexikon fur QA Engineers",
    intro:
      "Es gibt keine geschlossene Liste von Bugs, und genau das macht diesen Bereich stark. Hier ordnen wir reale Bugs nach Kategorie, Auswirkung, Ursprung und Erkennbarkeit, damit Lernen, Melden und Diskutieren leichter wird.",
    stats: [
      { value: "12", label: "Grundkategorien" },
      { value: "18", label: "dokumentierte Bugs" },
      { value: "5", label: "TheQAHub-Typen" },
    ],
    guideTitle: "So liest du dieses Lexikon",
    guide: [
      {
        title: "Mehrschichtige Klassifikation",
        text: "Derselbe Bug kann gleichzeitig funktional, kritisch und intermittierend sein. Deshalb zeigen wir mehrere Ebenen statt nur ein Label.",
      },
      {
        title: "Didaktischer Fokus",
        text: "Jeder Eintrag erklart, was es ist, wie er auftaucht, wie man ihn besser meldet und welche Signale fruher bei der Erkennung helfen.",
      },
      {
        title: "Fur Wachstum gebaut",
        text: "Die Struktur ist bereit fur neue Kategorien, gebrandete Typen und Vorschlage aus der Community.",
      },
    ],
    searchLabel: "Suche nach Name, Kategorie oder Symptom",
    searchPlaceholder: "Z. B. Login, Performance, Race Condition...",
    filterCategory: "Kategorie",
    filterImpact: "Auswirkung",
    allCategories: "Alle",
    allImpacts: "Alle",
    emptyTitle: "Keine Treffer",
    emptyText: "Versuche eine andere Filterkombination oder einen allgemeineren Begriff.",
    cardsTitle: "Dokumentierte Grundbugs",
    cardsIntro:
      "Diese erste Version verbindet technische Klassifikation mit klarer Sprache, damit sie fur Junior-Tester und reifere Teams nutzlich ist.",
    signatureEyebrow: "Bug-Subtypen als Signatur von",
    signatureIntro:
      "Diese Namen haben wir bei </TheQAHub> selbst erfunden, um reale Fehlermuster mit einer merkbaren, didaktischen und leicht teilbaren Sprache zu beschreiben.",
    expandTitle: "Hilf mit, die Taxonomie zu erweitern",
    expandText:
      "Wenn du eine neue Kategorie, einen wiederkehrenden Bug oder eine TheQAHub-Signatur vorschlagen willst, kannst du sie als Vorschlag senden. Dieser Bereich ist darauf ausgelegt, sich in Richtung Community-Beitrage, Kommentare oder kunftige Moderation zu entwickeln.",
    suggestionLabel: "Deine Bug- oder Klassifikationsidee",
    suggestionPlaceholder:
      "Beispiel: Chamaleon-Bug. Er tritt nur auf, wenn Rolle, Sprache und Viewport in derselben Sitzung gewechselt werden...",
    sendMail: "Vorschlag per E-Mail senden",
    contactCta: "Zum Kontakt",
    mailSubject: "Vorschlag fur TheQAHub Bug Dictionary",
    mailFallback: "Ich mochte einen neuen Eintrag fur das TheQAHub Bug Dictionary vorschlagen:",
    mailNote: "",
    fields: {
      origin: "Ursprung",
      detectability: "Erkennbarkeit",
      example: "Beispiel",
      report: "So meldest du ihn",
      detect: "So erkennst du ihn",
    },
    categories: ["Funktional", "UI", "UX", "Performance", "Sicherheit", "Kompatibilitat", "Integration", "API", "Logik", "Konkurrenz", "Intermittierend", "Daten", "Kritisch"],
    impacts: ["Kritisch", "Hoch", "Mittel", "Niedrig"],
  },
};

const bugEntries = {
  es: [
    {
      name: "Bug funcional",
      category: "Funcional",
      impact: "Alto",
      origin: "Frontend / Backend",
      detectability: "Fácil de reproducir",
      description: "El sistema no hace lo que deberia segun el requerimiento esperado.",
      example: "El boton de login no autentica aunque las credenciales sean validas.",
      report: "Describe el flujo exacto, el resultado esperado y el resultado real.",
      detect: "Tests E2E, smoke tests y validacion temprana de criterios de aceptacion.",
    },
    {
      name: "Bug de UI",
      category: "UI",
      categories: ["UI", "Compatibilidad"],
      impact: "Medio",
      origin: "Frontend",
      detectability: "Fácil de reproducir",
      description: "La interfaz se rompe visualmente o comunica informacion de forma incorrecta.",
      example: "Un boton principal queda cortado en movil y tapa parte del formulario.",
      report: "Incluye dispositivo, resolucion, navegador y captura visual.",
      detect: "Comparativas visuales, pruebas en distintos tamaños de pantalla y revisiones en varios navegadores.",
    },
    {
      name: "Bug de UX",
      category: "UX",
      impact: "Medio",
      origin: "Producto / Frontend",
      detectability: "Oculto",
      description: "La funcionalidad existe pero el flujo resulta confuso, lento o poco intuitivo.",
      example: "El usuario necesita cinco pasos para recuperar una contraseña simple.",
      report: "Explica en que punto aparece la friccion y por que afecta al objetivo del usuario.",
      detect: "Grabaciones de sesion, pruebas exploratorias y metricas de abandono del flujo.",
    },
    {
      name: "Bug de rendimiento",
      category: "Rendimiento",
      impact: "Alto",
      origin: "Frontend / Backend / Infraestructura",
      detectability: "Difícil",
      description: "La aplicación funciona, pero su tiempo de respuesta o consumo de recursos es anormal.",
      example: "El dashboard tarda 12 segundos en cargar al superar 200 usuarios concurrentes.",
      report: "Anade tiempos medidos, volumen de carga y entorno de ejecucion.",
      detect: "Mediciones de rendimiento, monitorizacion, pruebas de carga y alertas de consumo.",
    },
    {
      name: "Bug de seguridad",
      category: "Seguridad",
      categories: ["Seguridad", "Critico"],
      impact: "Critico",
      origin: "Backend / Infraestructura",
      detectability: "Oculto",
      description: "Existe una vulnerabilidad que puede exponer datos, permisos o comportamiento sensible.",
      example: "Un usuario puede acceder a recursos de otro cambiando un identificador en la URL.",
      report: "Documenta el vector, el riesgo y evita compartir datos sensibles reales.",
      detect: "Analisis automaticos de seguridad, pruebas de intrusion y revisiones de permisos y autorizacion.",
    },
    {
      name: "Bug de compatibilidad",
      category: "Compatibilidad",
      impact: "Alto",
      origin: "Frontend",
      detectability: "Difícil",
      description: "El sistema falla solo en ciertos navegadores, dispositivos o sistemas operativos.",
      example: "La subida de archivos funciona en Chrome pero falla en Safari iOS.",
      report: "Detalla navegador, version, dispositivo y si hay diferencias por locale.",
      detect: "Pruebas en varios navegadores, laboratorios de dispositivos y comprobaciones en entornos reales.",
    },
    {
      name: "Bug de integracion",
      category: "Integracion",
      categories: ["Integracion", "Datos"],
      impact: "Alto",
      origin: "Backend / Terceros",
      detectability: "Difícil",
      description: "La comunicacion entre sistemas, APIs o servicios externos produce resultados incoherentes.",
      example: "El checkout confirma la compra aunque el proveedor de pagos haya rechazado la transacción.",
      report: "Adjunta request, response, ids de correlacion y estado esperado entre sistemas.",
      detect: "Pruebas de contrato entre sistemas, simulaciones controladas y monitorizacion de extremo a extremo.",
    },
    {
      name: "Bug de logica",
      category: "Logica",
      impact: "Alto",
      origin: "Backend / Producto",
      detectability: "Fácil de reproducir",
      description: "Las reglas de negocio estan mal implementadas y producen decisiones incorrectas.",
      example: "Se aplica un descuento del 20 por ciento a usuarios que no cumplen la condicion.",
      report: "Relaciona claramente regla esperada, datos de entrada y calculo obtenido.",
      detect: "Pruebas unitarias, tablas de decision y validacion conjunta con negocio o producto.",
    },
    {
      name: "Bug de concurrencia",
      category: "Concurrencia",
      categories: ["Concurrencia", "Intermitente"],
      impact: "Critico",
      origin: "Backend / Infraestructura",
      detectability: "Intermitente",
      description: "Aparece cuando dos o mas procesos compiten por el mismo recurso o estado.",
      example: "Dos usuarios compran la ultima plaza disponible y ambos reciben confirmación.",
      report: "Anota timing, numero de actores y si hubo operaciones paralelas.",
      detect: "Pruebas de estres, trazas temporales y analisis de bloqueos o transacciónes simultaneas.",
    },
    {
      name: "Bug de datos",
      category: "Datos",
      impact: "Critico",
      origin: "Backend / Base de datos",
      detectability: "Oculto",
      description: "Los datos se corrompen, se duplican, se pierden o dejan de ser consistentes.",
      example: "El usuario actualiza su perfil y el sistema sobrescribe otro registro.",
      report: "Incluye antes y despues de la operacion, ids afectados y alcance del impacto.",
      detect: "Validaciones de integridad, auditorias y pruebas de migracion o sincronizacion de datos.",
    },
    {
      name: "Bug visual menor",
      category: "UI",
      impact: "Bajo",
      origin: "Frontend",
      detectability: "Fácil de reproducir",
      description: "Existe un detalle visual pequeño que no bloquea el flujo, pero da sensación de producto descuidado.",
      example: "Un icono aparece ligeramente desalineado respecto al texto en una tarjeta informativa.",
      report: "Indica la pantalla exacta, adjunta captura y explica si afecta a una resolucion concreta.",
      detect: "Revision visual, pruebas responsive y comprobaciones rapidas antes de publicar.",
    },
    {
      name: "Bug de microcopia",
      category: "UX",
      impact: "Bajo",
      origin: "Frontend / Contenido",
      detectability: "Fácil de reproducir",
      description: "El sistema muestra un texto mejorable, ambiguo o poco natural, aunque la funcionalidad siga operativa.",
      example: "Un mensaje de confirmación usa palabras confusas y hace dudar al usuario de si la acción termino bien.",
      report: "Señala el texto exacto, el contexto en el que aparece y por que puede generar confusion.",
      detect: "Revision funcional con foco en textos, validacion con negocio y lectura desde perspectiva de usuario final.",
    },
    {
      name: "Bug de workflow",
      category: "Funcional",
      categories: ["Funcional", "UX"],
      impact: "Alto",
      origin: "Frontend / Producto",
      detectability: "Fácil de reproducir",
      description: "Una secuencia de pasos rompe el flujo esperado y deja al usuario a mitad de camino.",
      example: "El usuario completa un asistente de alta, vuelve atras para corregir un dato y pierde todo el progreso.",
      report: "Explica en que paso exacto se rompe la continuidad, que habia completado antes y que informacion se pierde.",
      detect: "Pruebas end-to-end de flujos largos, caminos hacia atras y validacion de persistencia entre pasos.",
    },
    {
      name: "Bug de validacion",
      category: "Funcional",
      categories: ["Funcional", "UI"],
      impact: "Medio",
      origin: "Frontend / Backend",
      detectability: "Fácil de reproducir",
      description: "El sistema acepta o rechaza entradas de forma incorrecta y genera errores evitables.",
      example: "Un formulario permite enviar un email invalido o bloquea un telefono valido por una regla mal definida.",
      report: "Anota el dato usado, la validacion esperada, el mensaje mostrado y si el fallo ocurre en cliente o servidor.",
      detect: "Casos de prueba de equivalencia, valores frontera y revision de mensajes de error de formularios.",
    },
    {
      name: "Bug fuera de rango",
      category: "Logica",
      categories: ["Logica", "Datos"],
      impact: "Alto",
      origin: "Backend / Datos",
      detectability: "Fácil de reproducir",
      description: "El sistema no controla bien valores extremos y termina calculando o guardando datos imposibles.",
      example: "Una cantidad de `-1` o `999999` genera totales absurdos y el pedido sigue adelante.",
      report: "Describe el valor extremo usado, el resultado obtenido y que limites deberian haberse aplicado.",
      detect: "Pruebas de limites, particiones de equivalencia y validacion de reglas numericas antes de persistir datos.",
    },
    {
      name: "Bug de elemento roto",
      category: "UI",
      categories: ["UI", "Funcional"],
      impact: "Medio",
      origin: "Frontend",
      detectability: "Fácil de reproducir",
      description: "Un elemento visible parece utilizable, pero su comportamiento real esta roto o incompleto.",
      example: "Un boton secundario cambia de estado al hacer hover, pero al pulsarlo no ocurre nada.",
      report: "Senala donde esta el elemento, que promete visualmente y que respuesta real da al interactuar con el.",
      detect: "Recorridos exploratorios de interfaz, chequeos de interacción y contraste entre estados visuales y acción real.",
    },
    {
      name: "Bug API 500",
      category: "API",
      categories: ["API", "Integracion"],
      impact: "Critico",
      origin: "Backend / API",
      detectability: "Fácil de reproducir",
      description: "El servidor rompe internamente una operacion y responde con un error 500 sin gestionar bien el fallo.",
      example: "La creación de pedido llama a la API y devuelve `500 Internal Server Error` tras enviar datos válidos.",
      report: "Adjunta endpoint, payload enviado, codigo 500 recibido, correlacion si existe y momento exacto en que ocurre.",
      detect: "Contract testing, monitorizacion de errores en backend y validacion de respuestas no controladas en endpoints criticos.",
    },
    {
      name: "Bug API 400",
      category: "API",
      categories: ["API", "Funcional"],
      impact: "Alto",
      origin: "Backend / API",
      detectability: "Fácil de reproducir",
      description: "La API rechaza una solicitud con un 400 por validacion inconsistente, contrato roto o mensajes poco claros.",
      example: "Un endpoint devuelve `400 Bad Request` aunque el frontend esta enviando los campos esperados segun documentacion.",
      report: "Incluye endpoint, body, headers, respuesta completa y que parte del contrato parecia valida segun la spec.",
      detect: "Pruebas de contrato, colecciones de API, validacion de esquemas y comparacion entre documentacion y comportamiento real.",
    },
  ],
  en: [
    {
      name: "Functional bug",
      category: "Functional",
      impact: "High",
      origin: "Frontend / Backend",
      detectability: "Easy to reproduce",
      description: "The system does not do what it should according to the expected requirement.",
      example: "The login button does not authenticate even with valid credentials.",
      report: "Describe the exact flow, expected result and actual result.",
      detect: "E2E tests, smoke tests and early acceptance-criteria validation.",
    },
    {
      name: "UI bug",
      category: "UI",
      categories: ["UI", "Compatibility"],
      impact: "Medium",
      origin: "Frontend",
      detectability: "Easy to reproduce",
      description: "The interface breaks visually or communicates information incorrectly.",
      example: "A CTA is cut off on mobile and overlaps the form.",
      report: "Include device, viewport, browser and a visual capture.",
      detect: "Visual regression, responsive checks and cross-browser reviews.",
    },
    {
      name: "UX bug",
      category: "UX",
      impact: "Medium",
      origin: "Product / Frontend",
      detectability: "Hidden",
      description: "The feature exists but the flow feels confusing, slow or unintuitive.",
      example: "A user needs five steps to recover a simple password.",
      report: "Explain where friction appears and why it blocks the user goal.",
      detect: "Session replays, exploratory testing and abandonment metrics.",
    },
    {
      name: "Performance bug",
      category: "Performance",
      impact: "High",
      origin: "Frontend / Backend / Infrastructure",
      detectability: "Hard",
      description: "The application works, but response time or resource usage is abnormal.",
      example: "The dashboard takes 12 seconds to load above 200 concurrent users.",
      report: "Add measured timings, traffic volume and environment details.",
      detect: "Profiling, monitoring, load testing and resource alerts.",
    },
    {
      name: "Security bug",
      category: "Security",
      categories: ["Security", "Critical"],
      impact: "Critical",
      origin: "Backend / Infrastructure",
      detectability: "Hidden",
      description: "A vulnerability can expose data, permissions or sensitive behavior.",
      example: "A user can access another account by changing an identifier in the URL.",
      report: "Document the vector and risk without exposing real sensitive data.",
      detect: "SAST, DAST, pentesting and authorization reviews.",
    },
    {
      name: "Compatibility bug",
      category: "Compatibility",
      impact: "High",
      origin: "Frontend",
      detectability: "Hard",
      description: "The system fails only in certain browsers, devices or operating systems.",
      example: "File upload works in Chrome but fails in Safari iOS.",
      report: "Detail browser, version, device and locale differences.",
      detect: "Cross-browser matrices, device farms and real-environment checks.",
    },
    {
      name: "Integration bug",
      category: "Integration",
      categories: ["Integration", "Data"],
      impact: "High",
      origin: "Backend / Third parties",
      detectability: "Hard",
      description: "Communication between systems, APIs or external services returns inconsistent outcomes.",
      example: "Checkout confirms a purchase even though the payment provider rejected it.",
      report: "Attach request, response, correlation IDs and expected state between systems.",
      detect: "Contract testing, controlled mocks and distributed observability.",
    },
    {
      name: "Logic bug",
      category: "Logic",
      impact: "High",
      origin: "Backend / Product",
      detectability: "Easy to reproduce",
      description: "Business rules are implemented incorrectly and decisions come out wrong.",
      example: "A 20 percent discount is applied to users who do not meet the rule.",
      report: "Tie together expected rule, input data and actual calculation.",
      detect: "Unit tests, decision tables and business validation.",
    },
    {
      name: "Concurrency bug",
      category: "Concurrency",
      categories: ["Concurrency", "Intermittent"],
      impact: "Critical",
      origin: "Backend / Infrastructure",
      detectability: "Intermittent",
      description: "It appears when two or more processes compete for the same resource or state.",
      example: "Two users buy the last available slot and both receive confirmation.",
      report: "Note timing, number of actors and whether operations ran in parallel.",
      detect: "Stress tests, timing traces and lock or transaction analysis.",
    },
    {
      name: "Data bug",
      category: "Data",
      impact: "Critical",
      origin: "Backend / Database",
      detectability: "Hidden",
      description: "Data gets corrupted, duplicated, lost or becomes inconsistent.",
      example: "A profile update overwrites another user record.",
      report: "Include before and after state, affected IDs and impact scope.",
      detect: "Integrity validations, audits and migration testing.",
    },
    {
      name: "Minor visual bug",
      category: "UI",
      impact: "Low",
      origin: "Frontend",
      detectability: "Easy to reproduce",
      description: "There is a small visual issue that does not block the flow, but it makes the product feel less polished.",
      example: "An icon appears slightly misaligned with the text inside an informational card.",
      report: "Point to the exact screen, attach a capture and mention whether it happens at a specific resolution.",
      detect: "Visual review, responsive checks and quick pre-release inspections.",
    },
    {
      name: "Microcopy bug",
      category: "UX",
      impact: "Low",
      origin: "Frontend / Content",
      detectability: "Easy to reproduce",
      description: "The system shows wording that is unclear, awkward or less helpful even though the feature still works.",
      example: "A confirmation message uses confusing words and makes the user doubt whether the action finished correctly.",
      report: "Point out the exact text, the context where it appears and why it may create confusion.",
      detect: "Functional review focused on text, business validation and reading from the end-user perspective.",
    },
    {
      name: "Workflow bug",
      category: "Functional",
      categories: ["Functional", "UX"],
      impact: "High",
      origin: "Frontend / Product",
      detectability: "Easy to reproduce",
      description: "A sequence of steps breaks the expected flow and leaves the user stranded midway.",
      example: "A user completes a signup wizard, goes back to fix one field and loses all previous progress.",
      report: "Explain the exact step where continuity breaks, what had already been completed and what data gets lost.",
      detect: "End-to-end testing of long flows, back navigation paths and persistence checks between steps.",
    },
    {
      name: "Validation bug",
      category: "Functional",
      categories: ["Functional", "UI"],
      impact: "Medium",
      origin: "Frontend / Backend",
      detectability: "Easy to reproduce",
      description: "The system accepts or rejects input incorrectly and creates avoidable errors.",
      example: "A form allows an invalid email to pass or blocks a valid phone number because the rule is wrong.",
      report: "Note the input used, expected validation, shown message and whether the failure happens client-side or server-side.",
      detect: "Equivalence partitioning, boundary-value tests and review of form error messaging.",
    },
    {
      name: "Out-of-range bug",
      category: "Logic",
      categories: ["Logic", "Data"],
      impact: "High",
      origin: "Backend / Data",
      detectability: "Easy to reproduce",
      description: "The system fails to control extreme values and ends up calculating or storing impossible data.",
      example: "A quantity of `-1` or `999999` produces absurd totals and the order still goes through.",
      report: "Describe the extreme value used, obtained result and which limit should have blocked it.",
      detect: "Boundary tests, equivalence classes and validation of numeric rules before persisting data.",
    },
    {
      name: "Broken element bug",
      category: "UI",
      categories: ["UI", "Functional"],
      impact: "Medium",
      origin: "Frontend",
      detectability: "Easy to reproduce",
      description: "A visible element looks usable, but its real behavior is broken or incomplete.",
      example: "A secondary button changes on hover, but nothing happens when you click it.",
      report: "Point to where the element is, what it visually promises and what response it actually gives.",
      detect: "Exploratory UI sweeps, interaction checks and contrast between visual state and real action.",
    },
    {
      name: "API 500 bug",
      category: "API",
      categories: ["API", "Integration"],
      impact: "Critical",
      origin: "Backend / API",
      detectability: "Easy to reproduce",
      description: "The server breaks internally during an operation and responds with an unhandled 500 error.",
      example: "Order creation hits the API and returns `500 Internal Server Error` after sending valid data.",
      report: "Attach endpoint, sent payload, returned 500 code, correlation ID if available and exact timing.",
      detect: "Contract testing, backend error monitoring and validation of unhandled responses in critical endpoints.",
    },
    {
      name: "API 400 bug",
      category: "API",
      categories: ["API", "Functional"],
      impact: "High",
      origin: "Backend / API",
      detectability: "Easy to reproduce",
      description: "The API rejects a request with 400 due to inconsistent validation, a broken contract or unclear error messaging.",
      example: "An endpoint returns `400 Bad Request` even though the frontend sends the fields expected by the documentation.",
      report: "Include endpoint, body, headers, full response and which part of the contract looked valid according to the spec.",
      detect: "Contract tests, API collections, schema validation and comparison between docs and real behavior.",
    },
  ],
  de: [
    {
      name: "Funktionaler Bug",
      category: "Funktional",
      impact: "Hoch",
      origin: "Frontend / Backend",
      detectability: "Leicht reproduzierbar",
      description: "Das System macht nicht das, was laut Anforderung erwartet wird.",
      example: "Der Login-Button authentifiziert trotz gultiger Zugangsdaten nicht.",
      report: "Beschreibe den genauen Ablauf, das erwartete und das tatsachliche Ergebnis.",
      detect: "E2E-Tests, Smoke-Tests und fruhe Validierung von Akzeptanzkriterien.",
    },
    {
      name: "UI-Bug",
      category: "UI",
      categories: ["UI", "Kompatibilitat"],
      impact: "Mittel",
      origin: "Frontend",
      detectability: "Leicht reproduzierbar",
      description: "Die Oberflache bricht visuell oder kommuniziert Informationen falsch.",
      example: "Ein CTA wird auf Mobile abgeschnitten und uberlappt das Formular.",
      report: "Fuge Gerat, Viewport, Browser und einen Screenshot hinzu.",
      detect: "Visual Regression, Responsive Checks und Cross-Browser-Prufungen.",
    },
    {
      name: "UX-Bug",
      category: "UX",
      impact: "Mittel",
      origin: "Produkt / Frontend",
      detectability: "Versteckt",
      description: "Die Funktion existiert, aber der Ablauf ist verwirrend, langsam oder unintuitiv.",
      example: "Ein Nutzer braucht funf Schritte, um ein einfaches Passwort wiederherzustellen.",
      report: "Erklare, wo Reibung entsteht und warum sie das Nutzerziel blockiert.",
      detect: "Session Replays, exploratives Testen und Abbruchmetriken.",
    },
    {
      name: "Performance-Bug",
      category: "Performance",
      impact: "Hoch",
      origin: "Frontend / Backend / Infrastruktur",
      detectability: "Schwierig",
      description: "Die Anwendung funktioniert, aber Antwortzeit oder Ressourcenverbrauch sind auffallig.",
      example: "Das Dashboard braucht uber 12 Sekunden bei mehr als 200 gleichzeitigen Nutzern.",
      report: "Erganzte gemessene Zeiten, Lastvolumen und Umgebungsdetails.",
      detect: "Profiling, Monitoring, Lasttests und Ressourcenalarme.",
    },
    {
      name: "Sicherheits-Bug",
      category: "Sicherheit",
      categories: ["Sicherheit", "Kritisch"],
      impact: "Kritisch",
      origin: "Backend / Infrastruktur",
      detectability: "Versteckt",
      description: "Eine Schwachstelle kann Daten, Berechtigungen oder sensibles Verhalten offenlegen.",
      example: "Ein Nutzer greift durch das Andern einer URL-ID auf fremde Daten zu.",
      report: "Dokumentiere Vektor und Risiko, ohne echte sensible Daten offenzulegen.",
      detect: "SAST, DAST, Pentests und Autorisierungs-Reviews.",
    },
    {
      name: "Kompatibilitats-Bug",
      category: "Kompatibilitat",
      impact: "Hoch",
      origin: "Frontend",
      detectability: "Schwierig",
      description: "Das System fallt nur in bestimmten Browsern, Geraten oder Betriebssystemen aus.",
      example: "Datei-Upload funktioniert in Chrome, aber nicht in Safari iOS.",
      report: "Nenne Browser, Version, Gerat und Unterschiede nach Locale.",
      detect: "Cross-Browser-Matrizen, Device Farms und reale Umgebungen.",
    },
    {
      name: "Integrations-Bug",
      category: "Integration",
      categories: ["Integration", "Daten"],
      impact: "Hoch",
      origin: "Backend / Drittanbieter",
      detectability: "Schwierig",
      description: "Die Kommunikation zwischen Systemen, APIs oder externen Diensten liefert inkonsistente Ergebnisse.",
      example: "Der Checkout bestatigt einen Kauf, obwohl der Zahlungsanbieter ablehnt.",
      report: "Hange Request, Response, Korrelations-IDs und erwarteten Systemstatus an.",
      detect: "Contract Testing, kontrollierte Mocks und verteilte Observability.",
    },
    {
      name: "Logik-Bug",
      category: "Logik",
      impact: "Hoch",
      origin: "Backend / Produkt",
      detectability: "Leicht reproduzierbar",
      description: "Business-Regeln sind falsch implementiert und erzeugen falsche Entscheidungen.",
      example: "Ein Rabatt von 20 Prozent wird auf Nutzer angewendet, die die Bedingung nicht erfullen.",
      report: "Verknupfe erwartete Regel, Eingabedaten und tatsachliche Berechnung.",
      detect: "Unit-Tests, Entscheidungstabellen und Fachvalidierung.",
    },
    {
      name: "Konkurrenz-Bug",
      category: "Konkurrenz",
      categories: ["Konkurrenz", "Intermittierend"],
      impact: "Kritisch",
      origin: "Backend / Infrastruktur",
      detectability: "Intermittierend",
      description: "Er tritt auf, wenn zwei oder mehr Prozesse um dieselbe Ressource oder denselben Zustand konkurrieren.",
      example: "Zwei Nutzer kaufen den letzten verfugbaren Platz und beide erhalten eine Bestatigung.",
      report: "Notiere Timing, Anzahl der Akteure und parallele Operationen.",
      detect: "Stresstests, Zeitspuren sowie Lock- oder Transaktionsanalyse.",
    },
    {
      name: "Daten-Bug",
      category: "Daten",
      impact: "Kritisch",
      origin: "Backend / Datenbank",
      detectability: "Versteckt",
      description: "Daten werden beschadigt, dupliziert, verloren oder inkonsistent.",
      example: "Eine Profilaktualisierung uberschreibt einen fremden Datensatz.",
      report: "Fuge Vorher/Nachher-Zustand, betroffene IDs und den Wirkungsbereich hinzu.",
      detect: "Integritatsprufungen, Audits und Migrationstests.",
    },
    {
      name: "Kleiner visueller Bug",
      category: "UI",
      impact: "Niedrig",
      origin: "Frontend",
      detectability: "Leicht reproduzierbar",
      description: "Es gibt ein kleines visuelles Problem, das den Ablauf nicht blockiert, aber das Produkt weniger sauber wirken lasst.",
      example: "Ein Icon ist in einer Informationskarte leicht versetzt zum danebenstehenden Text.",
      report: "Nenne den genauen Screen, einen Screenshot und ob es nur bei einer bestimmten Auflosung passiert.",
      detect: "Visuelle Prufung, Responsive Checks und kurze Kontrollen vor dem Release.",
    },
    {
      name: "Microcopy-Bug",
      category: "UX",
      impact: "Niedrig",
      origin: "Frontend / Inhalt",
      detectability: "Leicht reproduzierbar",
      description: "Das System zeigt einen Text, der unklar, unpassend oder wenig hilfreich ist, obwohl die Funktion weiterhin arbeitet.",
      example: "Eine Bestatigungsnachricht ist so formuliert, dass Nutzer zweifeln, ob die Aktion wirklich abgeschlossen wurde.",
      report: "Zeige den genauen Text, den Kontext und warum er Verwirrung auslosen kann.",
      detect: "Funktionale Reviews mit Fokus auf Texte, Abstimmung mit Fachseite und Lesen aus Sicht des Endnutzers.",
    },
    {
      name: "Workflow-Bug",
      category: "Funktional",
      categories: ["Funktional", "UX"],
      impact: "Hoch",
      origin: "Frontend / Produkt",
      detectability: "Leicht reproduzierbar",
      description: "Eine Schrittfolge bricht den erwarteten Ablauf und lasst den Nutzer auf halbem Weg stehen.",
      example: "Ein Nutzer beendet fast einen Registrierungsassistenten, geht einen Schritt zuruck und verliert den gesamten Fortschritt.",
      report: "Erklare den genauen Schritt, an dem die Kontinuitat bricht, was schon ausgefullt war und was verloren geht.",
      detect: "End-to-End-Tests fur lange Flows, Back-Navigation und Persistenzprufungen zwischen Schritten.",
    },
    {
      name: "Validierungs-Bug",
      category: "Funktional",
      categories: ["Funktional", "UI"],
      impact: "Mittel",
      origin: "Frontend / Backend",
      detectability: "Leicht reproduzierbar",
      description: "Das System akzeptiert oder blockiert Eingaben falsch und erzeugt vermeidbare Fehler.",
      example: "Ein Formular lasst eine ungueltige E-Mail durch oder blockiert eine gueltige Telefonnummer wegen einer falschen Regel.",
      report: "Notiere Eingabe, erwartete Validierung, angezeigte Meldung und ob der Fehler im Client oder Server liegt.",
      detect: "Aquivalenzklassen, Grenzwerttests und Prufung der Fehlermeldungen in Formularen.",
    },
    {
      name: "Grenzwert-Bug",
      category: "Logik",
      categories: ["Logik", "Daten"],
      impact: "Hoch",
      origin: "Backend / Daten",
      detectability: "Leicht reproduzierbar",
      description: "Das System kontrolliert Extremwerte nicht sauber und berechnet oder speichert unmogliche Daten.",
      example: "Eine Menge von `-1` oder `999999` erzeugt absurde Summen und die Bestellung lauft trotzdem weiter.",
      report: "Beschreibe den Extremwert, das Ergebnis und welche Grenze ihn hatte stoppen mussen.",
      detect: "Grenzwerttests, Aquivalenzklassen und Validierung numerischer Regeln vor dem Speichern.",
    },
    {
      name: "Defektes-Element-Bug",
      category: "UI",
      categories: ["UI", "Funktional"],
      impact: "Mittel",
      origin: "Frontend",
      detectability: "Leicht reproduzierbar",
      description: "Ein sichtbares Element wirkt benutzbar, aber sein echtes Verhalten ist defekt oder unvollstandig.",
      example: "Ein sekundarer Button reagiert optisch auf Hover, tut beim Klick aber gar nichts.",
      report: "Zeige, wo das Element ist, was es visuell verspricht und was tatsachlich passiert.",
      detect: "Explorative UI-Prufungen, Interaktionschecks und Vergleich zwischen visueller Anzeige und echter Aktion.",
    },
    {
      name: "API-500-Bug",
      category: "API",
      categories: ["API", "Integration"],
      impact: "Kritisch",
      origin: "Backend / API",
      detectability: "Leicht reproduzierbar",
      description: "Der Server bricht intern wahrend einer Operation und antwortet mit einem unbehandelten 500-Fehler.",
      example: "Die Bestellungserstellung ruft die API auf und bekommt `500 Internal Server Error`, obwohl die Daten gueltig sind.",
      report: "Hange Endpoint, Payload, 500-Code, Korrelations-ID falls vorhanden und exakten Zeitpunkt an.",
      detect: "Contract Tests, Backend-Fehlermonitoring und Prufung unbehandelter Antworten auf kritischen Endpoints.",
    },
    {
      name: "API-400-Bug",
      category: "API",
      categories: ["API", "Funktional"],
      impact: "Hoch",
      origin: "Backend / API",
      detectability: "Leicht reproduzierbar",
      description: "Die API lehnt eine Anfrage mit 400 ab, weil Validierung, Vertrag oder Fehlermeldung inkonsistent sind.",
      example: "Ein Endpoint liefert `400 Bad Request`, obwohl das Frontend laut Dokumentation die erwarteten Felder sendet.",
      report: "Nenne Endpoint, Body, Header, komplette Antwort und welcher Teil des Vertrags laut Spec gueltig wirkte.",
      detect: "Contract Tests, API-Collections, Schema-Validierung und Vergleich zwischen Doku und echtem Verhalten.",
    },
  ],
};

const signatureEntries = {
  es: [
    { name: "Bug fantasma", text: "Aparece en producción o en demos, pero nadie logra reproducirlo con consistencia.", why: "En TheQAHub lo usamos para nombrar ese fallo escurridizo que todo equipo ha sufrido y nadie sabe acotar bien al principio.", context: "Sirve para explicar que el problema existe de verdad, aunque dependa de timing, caches, datos o entornos muy concretos.", usage: "Es una etiqueta de comunicación. Ayuda a decir: esto no está resuelto, solo aún no sabemos atraparlo bien.", example: "Un checkout falla una vez cada varios días en producción, pero nunca en staging ni cuando lo pruebas manualmente." },
    { name: "Bug suicida", text: "Una acción mínima desencadena un fallo desproporcionado y rompe una parte crítica.", why: "Lo inventamos en TheQAHub para poner nombre a esos bugs que parecen pequeños pero provocan un impacto enorme e inmediato.", context: "Funciona muy bien en formación y retrospectivas porque transmite peligro real con un término muy visual.", usage: "Se usa cuando una acción aparentemente inocente rompe una zona sensible del sistema.", example: "Editar un campo opcional del perfil tumba la sesión completa del usuario y lo expulsa de la aplicación." },
    { name: "Bug muro", text: "Bloquea por completo el flujo del usuario y no deja alternativa razonable.", why: "TheQAHub lo define para diferenciar claramente un simple fallo molesto de un bloqueo total del camino del usuario.", context: "Nos interesa porque comunica negocio, urgencia y frustración del usuario en una sola expresión.", usage: "Encaja cuando no hay workaround real y el usuario queda encerrado en un punto del flujo.", example: "No puedes avanzar del paso 2 de un onboarding porque el botón continuar nunca se habilita." },
    { name: "Bug efecto dominó", text: "Arreglas uno y emergen varios fallos encadenados en áreas cercanas.", why: "Es una firma de TheQAHub para hablar de deuda técnica, acoplamiento y regresiones de forma muy fácil de recordar.", context: "Nos ayuda a educar al equipo: no siempre estás arreglando un bug aislado, a veces estás tocando una cadena entera.", usage: "Se usa cuando un fix aparentemente pequeño destapa varios comportamientos colaterales.", example: "Corriges una regla de precios y de pronto se rompen cupones, impuestos y mensajes del resumen final." },
    { name: "Bug silencioso", text: "No explota de forma visible, pero degrada rendimiento o calidad poco a poco.", why: "En TheQAHub nos gusta este nombre porque pone foco en los fallos que no gritan, pero erosionan la experiencia y la confianza.", context: "Es ideal para contenido educativo, porque enseña que no todo bug llega con error rojo o pantalla rota.", usage: "Describe problemas que pasan desapercibidos al principio pero dejan huella en consumo, tiempos o calidad percibida.", example: "Una pantalla sigue funcionando, pero cada visita consume más memoria y tarda un poco más en responder." },
  ],
  en: [
    { name: "Ghost bug", text: "It shows up in production or demos, but nobody can reproduce it consistently.", why: "At TheQAHub we use it to name the slippery kind of failure teams know is real but still cannot pin down.", context: "It explains that the issue exists, even when timing, caches, data or environment conditions make it elusive.", usage: "It is a communication label: the bug is not solved, it is simply not trapped yet.", example: "Checkout fails once every few days in production, but never in staging or manual retests." },
    { name: "Suicide bug", text: "A tiny action triggers a disproportionate failure and breaks a critical area.", why: "We coined it at TheQAHub for those bugs where a small action causes massive damage.", context: "It works well in training and retros because the term instantly signals danger and scale.", usage: "Use it when something seemingly minor detonates a critical system behavior.", example: "Editing one optional profile field kills the full session and throws the user out of the app." },
    { name: "Wall bug", text: "It fully blocks the user flow and leaves no reasonable workaround.", why: "TheQAHub uses this to separate a merely annoying issue from a total flow blocker.", context: "It communicates business impact, urgency and user frustration in one memorable phrase.", usage: "Use it when there is no real workaround and the user is trapped.", example: "You cannot move past step 2 of onboarding because the continue button never activates." },
    { name: "Domino bug", text: "You fix one issue and several chained failures appear nearby.", why: "It is a TheQAHub signature for talking about coupling, hidden regressions and technical debt.", context: "It teaches teams that some bugs are not isolated at all, they belong to a whole chain.", usage: "Use it when one fix reveals multiple side effects in neighboring areas.", example: "You fix pricing rules and suddenly coupons, taxes and summary messages all start failing." },
    { name: "Silent bug", text: "It does not visibly explode, but it degrades performance or quality over time.", why: "We like this term at TheQAHub because it highlights failures that do not scream but still erode trust.", context: "It is especially useful for educational content because not every bug arrives with a red error screen.", usage: "Use it for issues that stay hidden at first but slowly damage speed, quality or confidence.", example: "A screen keeps working, but every visit consumes more memory and responds a bit slower." },
  ],
  de: [
    { name: "Geister-Bug", text: "Er erscheint in Produktion oder Demos, aber niemand kann ihn konsistent reproduzieren.", why: "Bei TheQAHub nutzen wir ihn fur Fehler, die alle kennen, aber niemand sauber einfangen kann.", context: "Der Begriff zeigt: Das Problem ist real, auch wenn Timing, Cache, Daten oder Umgebung es schwer greifbar machen.", usage: "Er ist ein Kommunikationsbegriff: nicht gelost, sondern noch nicht sauber eingefangen.", example: "Ein Checkout scheitert nur gelegentlich in Produktion, aber nie in Staging oder bei manuellen Retests." },
    { name: "Selbstmord-Bug", text: "Eine minimale Aktion lost einen unverhaltnismassigen kritischen Fehler aus.", why: "TheQAHub hat ihn fur Bugs gepragt, bei denen eine kleine Aktion unverhaltnismassig grossen Schaden anrichtet.", context: "Er funktioniert gut in Schulung und Review, weil der Begriff Gefahr sofort vermittelt.", usage: "Nutze ihn, wenn ein scheinbar kleiner Trigger einen kritischen Bereich sprengt.", example: "Das Andern eines optionalen Profilfeldes beendet die ganze Sitzung des Nutzers." },
    { name: "Mauer-Bug", text: "Er blockiert den Nutzerfluss komplett und lasst keinen sinnvollen Ausweg.", why: "Damit trennt TheQAHub klar zwischen einem laestigen Fehler und einer totalen Blockade.", context: "Der Begriff transportiert Geschaftsrisiko, Dringlichkeit und Frust in einer Formulierung.", usage: "Nutze ihn, wenn kein echter Workaround existiert und der Nutzer feststeckt.", example: "Im Onboarding bleibt der Nutzer bei Schritt 2 stecken, weil der Weiter-Button nie aktiv wird." },
    { name: "Domino-Bug", text: "Du behebst einen Fehler und mehrere verkettete Probleme tauchen daneben auf.", why: "Das ist eine TheQAHub-Signatur fur Kopplung, versteckte Regressionen und technische Schuld.", context: "Er hilft Teams zu sehen, dass manche Fehler keine Einzelfalle sind, sondern Kettenreaktionen.", usage: "Nutze ihn, wenn ein Fix weitere Seiteneffekte in benachbarten Bereichen freilegt.", example: "Eine Korrektur in der Preislogik sorgt plotzlich fur Probleme bei Gutscheinen, Steuern und Summen." },
    { name: "Leiser Bug", text: "Er explodiert nicht sichtbar, verschlechtert aber mit der Zeit Leistung oder Qualitat.", why: "TheQAHub mag diesen Begriff, weil er auf Fehler zeigt, die nicht schreien und dennoch Vertrauen abbauen.", context: "Er ist stark fur Lerninhalte, weil nicht jeder Bug mit rotem Error und Absturz erscheint.", usage: "Nutze ihn fur Probleme, die zuerst unauffallig sind, aber Geschwindigkeit oder Qualitat langsam verschlechtern.", example: "Ein Screen funktioniert weiter, braucht aber bei jedem Besuch etwas mehr Speicher und wird langsamer." },
  ],
};

const modalCopy = {
  es: {
    openDemo: "Clicka encima para ver el bug en vivo",
    close: "Cerrar",
    email: "Email",
    password: "Contraseña",
    signIn: "Iniciar sesion",
    invalidCredentials: "Error: credenciales invalidas",
    loginFailed: "No se pudo iniciar sesion",
    mobile: "Movil",
    desktop: "Ordenador",
    name: "Nombre",
    createAccount: "Crear cuenta",
    phone: "Telefono",
    liveDemo: "Demo interactiva",
    stepsTitle: "Como reproducirlo",
    expectedTitle: "Que deberia pasar",
    actualTitle: "Que pasa con el bug",
    learnTitle: "Qué aprendes aquí",
    reset: "Reiniciar demo",
    watchLabel: "Fijate en:",
  },
  en: {
    openDemo: "Click to view the bug live",
    close: "Close",
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    invalidCredentials: "Error: invalid credentials",
    loginFailed: "Could not sign in",
    mobile: "Mobile",
    desktop: "Desktop",
    name: "Name",
    createAccount: "Create account",
    phone: "Phone",
    liveDemo: "Interactive demo",
    stepsTitle: "How to reproduce it",
    expectedTitle: "What should happen",
    actualTitle: "What happens with the bug",
    learnTitle: "What you learn here",
    reset: "Reset demo",
    watchLabel: "Watch for:",
  },
  de: {
    openDemo: "Klicke hier, um den Bug live zu sehen",
    close: "Schliessen",
    email: "E-Mail",
    password: "Passwort",
    signIn: "Anmelden",
    invalidCredentials: "Fehler: ungultige Zugangsdaten",
    loginFailed: "Anmeldung nicht moglich",
    mobile: "Mobil",
    desktop: "Desktop",
    name: "Name",
    createAccount: "Konto erstellen",
    phone: "Telefon",
    liveDemo: "Interaktive Demo",
    stepsTitle: "So reproduzierst du ihn",
    expectedTitle: "Was passieren sollte",
    actualTitle: "Was mit dem Bug passiert",
    learnTitle: "Was du hier lernst",
    reset: "Demo zurucksetzen",
    watchLabel: "Achte auf:",
  },
};

const demoGuides = {
  es: [
    {
      steps: ["Escribe `qa@theqahub.es` y `bug123`.", "Pulsa en iniciar sesion.", "Observa que la aplicación falla aunque los datos son correctos."],
      expected: "Con credenciales validas, el usuario deberia entrar al panel.",
      actual: "El formulario devuelve error y bloquea el acceso.",
      learn: "Un bug funcional rompe el objetivo principal del flujo aunque la interfaz parezca correcta.",
      watch: "Si el sistema valida bien los datos pero ejecuta mal la acción final.",
    },
    {
      steps: ["Activa la vista movil.", "Baja hasta el formulario.", "Mira como el boton invade el campo inferior."],
      expected: "El boton principal deberia respetar el espaciado y no tapar ningun campo.",
      actual: "El boton se monta sobre el formulario y dificulta completar la acción.",
      learn: "Los bugs de UI suelen verse rapido, pero hay que reproducirlos en el contexto exacto: viewport, dispositivo y densidad.",
      watch: "El comportamiento del layout cuando cambia el tamaño de pantalla.",
    },
    {
      steps: ["Avanza por la recuperacion de password.", "Cuenta los pasos y lee los textos.", "Valora si el flujo es claro o si se hace eterno."],
      expected: "El usuario deberia recuperar acceso en pocos pasos y con mensajes claros.",
      actual: "El flujo da demasiadas vueltas y genera friccion innecesaria.",
      learn: "Un bug de UX no siempre rompe la funcionalidad, pero si rompe la experiencia y la conversión.",
      watch: "Cuanta friccion aparece para completar una tarea sencilla.",
    },
    {
      steps: ["Pulsa en cargar dashboard.", "Observa la barra de progreso.", "Comprueba cuanto tarda en responder."],
      expected: "La pantalla deberia cargar de forma fluida con tiempos razonables.",
      actual: "La respuesta se alarga y la sensación de bloqueo crece demasiado.",
      learn: "En rendimiento importa tanto el tiempo real como la percepcion del usuario frente a la espera.",
      watch: "Si la latencia degrada una acción frecuente o critica.",
    },
    {
      steps: ["Deja el ID propio por defecto.", "Prueba a cambiarlo por otro ID.", "Comprueba si puedes ver datos ajenos."],
      expected: "El sistema deberia impedir acceder a recursos de otro usuario aunque cambies el identificador.",
      actual: "Modificar el ID expone informacion que no deberias poder consultar.",
      learn: "Muchos bugs de seguridad aparecen cuando el backend confia demasiado en lo que manda el cliente.",
      watch: "Si el sistema te deja consultar datos que no pertenecen a tu sesion.",
    },
    {
      steps: ["Elige Chrome o Safari.", "Simula una subida de archivo.", "Compara el resultado entre navegadores."],
      expected: "La misma acción deberia comportarse igual en los entornos soportados.",
      actual: "En Safari la subida falla aunque el flujo parezca correcto en Chrome.",
      learn: "Los bugs de compatibilidad solo aparecen cuando pruebas donde duele: navegador, OS o dispositivo real.",
      watch: "Las diferencias de comportamiento entre entornos aparentemente equivalentes.",
    },
    {
      steps: ["Confirma una compra.", "Mira la respuesta del proveedor de pagos.", "Revisa el estado final que guarda la app."],
      expected: "Si el proveedor rechaza el pago, el pedido no deberia quedar confirmado.",
      actual: "La aplicación confirma la compra aunque la integracion externa la haya rechazado.",
      learn: "En integraciones hay que validar la verdad del sistema externo, no solo el estado bonito de la interfaz.",
      watch: "Si la app y el servicio tercero terminan con estados inconsistentes.",
    },
    {
      steps: ["Mantente como usuario estandar.", "Marca un pedido superior a 100 euros.", "Calcula el descuento aplicado."],
      expected: "El descuento solo deberia activarse cuando se cumple la regla real de negocio.",
      actual: "La regla se aplica mal y concede un beneficio que no corresponde.",
      learn: "Los bugs de logica se detectan contrastando reglas, datos de entrada y resultado final.",
      watch: "Si la salida refleja exactamente la condicion definida por negocio.",
    },
    {
      steps: ["Lanza la reserva simultanea.", "Observa a los dos usuarios comprando a la vez.", "Revisa el stock final."],
      expected: "Solo una reserva deberia confirmarse si quedaba una plaza libre.",
      actual: "Ambas operaciones acaban como exitosas y se vende mas de lo disponible.",
      learn: "La concurrencia rompe sistemas que parecian correctos cuando solo habia un actor.",
      watch: "Si dos procesos pisan el mismo recurso sin coordinacion real.",
    },
    {
      steps: ["Edita el perfil de Ana.", "Guarda un nuevo nombre.", "Comprueba que ficha se modifica realmente."],
      expected: "Solo deberia cambiarse el registro del usuario que estas editando.",
      actual: "La actualizacion pisa otro registro y corrompe los datos.",
      learn: "Los bugs de datos suelen ser traicioneros porque su impacto permanece aunque cierres la pantalla.",
      watch: "Si el cambio persiste sobre el identificador correcto.",
    },
    {
      steps: ["Observa la tarjeta tal como aparece.", "Activa la comparacion.", "Compara alineacion actual frente a esperada."],
      expected: "Icono y texto deberian compartir eje visual y dar sensación de orden.",
      actual: "El desalineado no bloquea, pero transmite baja calidad.",
      learn: "Los bugs bajos no bloquean negocio, pero si afectan a la percepcion de producto.",
      watch: "Esos pequeños detalles que un usuario si nota aunque no los nombre tecnicamente.",
    },
    {
      steps: ["Pulsa en guardar cambios.", "Lee el mensaje final.", "Decide si realmente entiendes si la acción acabo bien."],
      expected: "La confirmación deberia ser directa y dejar claro el resultado.",
      actual: "El mensaje es ambiguo y deja al usuario con dudas.",
      learn: "La microcopia tambien es calidad: un texto confuso puede disparar errores de uso y soporte.",
      watch: "Si el mensaje reduce incertidumbre o la aumenta.",
    },
  ],
  en: [
    {
      steps: ["Enter `qa@theqahub.es` and `bug123`.", "Click sign in.", "Notice how access fails even with valid data."],
      expected: "Valid credentials should open the dashboard.",
      actual: "The form shows an error and blocks access.",
      learn: "A functional bug breaks the core goal of the flow even when the UI looks correct.",
      watch: "whether the final action behaves correctly after valid input.",
    },
    {
      steps: ["Enable mobile view.", "Scroll to the form.", "See how the main button overlaps the lower field."],
      expected: "The main button should keep proper spacing and never cover inputs.",
      actual: "The button overlaps the form and makes completion harder.",
      learn: "UI bugs are often easy to spot, but only in the exact viewport where they appear.",
      watch: "how layout reacts when screen size changes.",
    },
    {
      steps: ["Go through password recovery.", "Count the steps and read the copy.", "Decide whether the path feels intuitive."],
      expected: "The user should recover access quickly and clearly.",
      actual: "The flow drags on and creates unnecessary friction.",
      learn: "A UX bug may not break functionality, but it breaks clarity and momentum.",
      watch: "how much friction appears for a simple task.",
    },
    {
      steps: ["Click load dashboard.", "Watch the progress bar.", "Measure how long the response takes."],
      expected: "The screen should load smoothly within a reasonable time.",
      actual: "The wait is too long and feels blocking.",
      learn: "Performance is both measured time and perceived delay.",
      watch: "whether latency damages a frequent or critical action.",
    },
    {
      steps: ["Keep your own ID first.", "Replace it with another user ID.", "Check whether private data becomes visible."],
      expected: "Changing the identifier should never bypass authorization.",
      actual: "Editing the ID exposes another user's data.",
      learn: "Security bugs often appear when the server trusts client input too much.",
      watch: "whether permissions are enforced on the backend.",
    },
    {
      steps: ["Choose Chrome or Safari.", "Simulate a file upload.", "Compare the outcome between browsers."],
      expected: "The same action should behave consistently across supported environments.",
      actual: "Safari fails while Chrome seems fine.",
      learn: "Compatibility bugs only show up when you test where they really live.",
      watch: "behavior differences between seemingly similar environments.",
    },
    {
      steps: ["Confirm a purchase.", "Read the payment provider response.", "Review the final order state in the app."],
      expected: "A rejected payment should never produce a confirmed order.",
      actual: "The app confirms the order despite the external rejection.",
      learn: "Integrations require validating the source of truth, not just the pretty UI state.",
      watch: "whether system state and provider state diverge.",
    },
    {
      steps: ["Stay as a standard user.", "Set an order above 100 euros.", "Review the applied discount."],
      expected: "The discount should only apply when the real rule is satisfied.",
      actual: "The rule is wrong and grants an invalid discount.",
      learn: "Logic bugs are found by contrasting rules, input data and final output.",
      watch: "whether the result strictly follows business conditions.",
    },
    {
      steps: ["Launch parallel booking.", "Watch both users reserve at once.", "Check the final stock."],
      expected: "Only one booking should succeed if one seat remained.",
      actual: "Both actions succeed and oversell the inventory.",
      learn: "Concurrency issues appear when correct-looking systems face simultaneous actors.",
      watch: "whether two processes can mutate the same resource unsafely.",
    },
    {
      steps: ["Edit Ana's profile.", "Save a new name.", "Check which record changes."],
      expected: "Only the edited user's record should update.",
      actual: "Another record gets overwritten and data is corrupted.",
      learn: "Data bugs are dangerous because their impact outlives the current screen.",
      watch: "whether persistence targets the correct identifier.",
    },
    {
      steps: ["Look at the card as rendered.", "Enable comparison.", "Compare actual alignment versus expected alignment."],
      expected: "Icon and text should share the same visual rhythm.",
      actual: "The misalignment does not block anything, but it hurts polish.",
      learn: "Low-impact bugs still shape product perception.",
      watch: "small details users notice even without technical words.",
    },
    {
      steps: ["Click save changes.", "Read the confirmation copy.", "Decide whether the result is really clear."],
      expected: "The confirmation should clearly communicate success.",
      actual: "The wording is ambiguous and creates doubt.",
      learn: "Microcopy is quality too: confusing text drives mistakes and support noise.",
      watch: "whether the message reduces uncertainty or increases it.",
    },
  ],
};

function FunctionalBugDemo({ ui }) {
  const [email, setEmail] = useState("qa@theqahub.es");
  const [password, setPassword] = useState("bug123");
  const [message, setMessage] = useState("");

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-panel">
        <label>{ui.email}</label>
        <input value={email} onChange={(event) => setEmail(event.target.value)} />
        <label>{ui.password}</label>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        <button
          type="button"
          className="bug-demo-action"
          onClick={() => setMessage(email === "qa@theqahub.es" && password === "bug123" ? ui.invalidCredentials : ui.loginFailed)}
        >
          {ui.signIn}
        </button>
      </div>
      <div className={`bug-demo-status ${message ? "error" : ""}`}>{message || ui.expectedTitle}</div>
    </div>
  );
}

function UiBugDemo({ ui }) {
  const [mobile, setMobile] = useState(true);

  return (
      <div className="bug-demo-surface">
      <div className="bug-demo-toolbar">
        <button type="button" className={mobile ? "active" : ""} onClick={() => setMobile(true)}>{ui.mobile}</button>
        <button type="button" className={!mobile ? "active" : ""} onClick={() => setMobile(false)}>{ui.desktop}</button>
      </div>
      <div className={`bug-ui-frame ${mobile ? "mobile" : "desktop"}`}>
        <div className="bug-ui-card">
          <div className="bug-ui-field">{ui.name}</div>
          <div className="bug-ui-field">{ui.email}</div>
          <button type="button" className="bug-ui-cta">{ui.createAccount}</button>
          <div className="bug-ui-field">{ui.phone}</div>
        </div>
      </div>
    </div>
  );
}

function UxBugDemo() {
  const [step, setStep] = useState(1);
  const steps = ["Introduce tu email", "Confirma tu email", "Responde a una pregunta", "Espera un codigo", "Crea nueva password"];

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-progress">
        {steps.map((item, index) => (
          <div key={item} className={index + 1 === step ? "current" : index + 1 < step ? "done" : ""}>
            <span>{index + 1}</span>
            <small>{item}</small>
          </div>
        ))}
      </div>
      <div className="bug-demo-note">
        <p>{steps[step - 1]}</p>
        <button type="button" className="bug-demo-action" onClick={() => setStep((value) => (value === 5 ? 1 : value + 1))}>
          {step === 5 ? "Volver a empezar" : "Siguiente paso"}
        </button>
      </div>
    </div>
  );
}

function PerformanceBugDemo() {
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [email, setEmail] = useState("qa@theqahub.es");
  const [password, setPassword] = useState("bug123");

  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setElapsed((value) => value + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [loading]);

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-panel">
        <label>Email</label>
        <input value={email} onChange={(event) => setEmail(event.target.value)} />
        <label>Password</label>
        <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        <button
          type="button"
          className="bug-demo-action"
          onClick={() => {
            setElapsed(0);
            setLoading(true);
          }}
          disabled={loading}
        >
          {loading ? "Intentando entrar..." : "Entrar al dashboard"}
        </button>
      </div>
      {loading ? (
        <div className="bug-demo-status warning spinner-card">
          <span className="bug-spinner" />
          <div>
            <strong>Cargando sesion...</strong>
            <p>{elapsed < 18 ? `La app sigue pensando... ${elapsed}s` : "Han pasado mas de 18 segundos y el spinner sigue girando sin responder."}</p>
          </div>
        </div>
      ) : (
        <div className="bug-demo-status">Las credenciales ya estan correctas. El problema es la espera infinita tras el login.</div>
      )}
    </div>
  );
}

function SecurityBugDemo() {
  const [userId, setUserId] = useState("1001");
  const [loadedProfile, setLoadedProfile] = useState(null);
  const profiles = {
    "1001": { owner: "Ana Tester", access: "Tu perfil", plan: "Plan Starter" },
    "1002": { owner: "Luis Admin", access: "Perfil expuesto", plan: "Plan Enterprise" },
  };

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-panel">
          <label>Usuario autenticado</label>
          <input value="1001" disabled />
          <label>ID solicitado en la URL</label>
          <input value={userId} onChange={(event) => setUserId(event.target.value)} />
          <small>Prueba a cambiar `1001` por `1002` y luego carga el perfil.</small>
          <button type="button" className="bug-demo-action" onClick={() => setLoadedProfile(profiles[userId] || null)}>
            Ver perfil
          </button>
        </div>
        <div className={`bug-demo-box ${loadedProfile?.access === "Perfil expuesto" ? "danger" : ""}`}>
          <strong>Respuesta visible</strong>
          {loadedProfile ? (
            <>
              <p>{loadedProfile.access}</p>
              <p>Propietario: {loadedProfile.owner}</p>
              <p>Plan: {loadedProfile.plan}</p>
            </>
          ) : (
            <p>Aun no has cargado ningun perfil.</p>
          )}
        </div>
      </div>
      {loadedProfile?.access === "Perfil expuesto" && (
        <div className="bug-demo-status error">Aunque sigues autenticado como 1001, el sistema te ha dejado ver informacion privada del 1002.</div>
      )}
    </div>
  );
}

function CompatibilityBugDemo() {
  const [browser, setBrowser] = useState("Chrome");
  const [uploaded, setUploaded] = useState(false);

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-toolbar">
        {["Chrome", "Safari"].map((item) => (
          <button key={item} type="button" className={browser === item ? "active" : ""} onClick={() => { setBrowser(item); setUploaded(false); }}>
            {item}
          </button>
        ))}
      </div>
      <div className="bug-demo-panel">
        <button type="button" className="bug-demo-action" onClick={() => setUploaded(true)}>Subir archivo</button>
      </div>
      {uploaded && <div className={`bug-demo-status ${browser === "Safari" ? "error" : "success"}`}>{browser === "Safari" ? "Error: formato no soportado" : "Archivo subido correctamente"}</div>}
    </div>
  );
}

function IntegrationBugDemo() {
  const [result, setResult] = useState(null);

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-box">
          <strong>Proveedor de pago</strong>
          <p>{result ? "X Pago rechazado" : "Pendiente"}</p>
        </div>
        <div className="bug-demo-box">
          <strong>Estado en la app</strong>
          <p>{result ? "✓ Compra confirmada" : "Pendiente"}</p>
        </div>
      </div>
      <button type="button" className="bug-demo-action" onClick={() => setResult(true)} disabled={result}>
        {result ? "Compra confirmada" : "Confirmar compra"}
      </button>
      {result && <div className="bug-demo-status error">La interfaz da la compra por buena, pero el proveedor ha rechazado el pago. Los estados no cuadran.</div>}
    </div>
  );
}

function LogicBugDemo() {
  const [userType, setUserType] = useState("standard");
  const [amountInput, setAmountInput] = useState("120");
  const amount = Number(amountInput || 0);
  const shouldDiscount = userType === "premium" && amount >= 100;
  const buggyDiscount = amount >= 100;
  const total = amount - (buggyDiscount ? amount * 0.2 : 0);

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-panel">
          <label>Tipo de usuario</label>
          <select value={userType} onChange={(event) => setUserType(event.target.value)}>
            <option value="standard">Estandar</option>
            <option value="premium">Premium</option>
          </select>
          <label>Pedido</label>
          <input
            type="text"
            inputMode="numeric"
            value={amountInput}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (/^\d*$/.test(nextValue)) {
                setAmountInput(nextValue);
              }
            }}
          />
        </div>
        <div className="bug-demo-box">
          <strong>Regla esperada</strong>
          <p>{shouldDiscount ? "Descuento valido" : "No deberia haber descuento"}</p>
          <strong>Resultado buggy</strong>
          <p>Total final: {amountInput === "" ? "--" : `${total.toFixed(2)} EUR`}</p>
        </div>
      </div>
    </div>
  );
}

function ConcurrencyBugDemo() {
  const [result, setResult] = useState(false);

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className={`bug-demo-box ${result ? "success" : ""}`}><strong>Usuario A</strong><p>{result ? "Reserva confirmada" : "Esperando"}</p></div>
        <div className={`bug-demo-box ${result ? "success" : ""}`}><strong>Usuario B</strong><p>{result ? "Reserva confirmada" : "Esperando"}</p></div>
      </div>
      <div className="bug-demo-status error">{result ? "Stock restante: -1 plaza" : "Stock restante: 1 plaza"}</div>
      <button type="button" className="bug-demo-action" onClick={() => setResult(true)}>Lanzar reserva simultanea</button>
    </div>
  );
}

function DataBugDemo() {
  const [anaName, setAnaName] = useState("Ana");
  const [records, setRecords] = useState({ ana: "Ana", luis: "Luis" });

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-panel">
          <label>Editando a Ana</label>
          <input value={anaName} onChange={(event) => setAnaName(event.target.value)} />
          <button type="button" className="bug-demo-action" onClick={() => setRecords((value) => ({ ...value, luis: anaName }))}>Guardar cambios</button>
        </div>
        <div className="bug-demo-box">
          <strong>Registros</strong>
          <p>Ana: {records.ana}</p>
          <p>Luis: {records.luis}</p>
        </div>
      </div>
    </div>
  );
}

function VisualMinorBugDemo() {
  const [compare, setCompare] = useState(false);

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-box">
          <strong>Actual</strong>
          <div className="bug-icon-row broken"><span>i</span><p>Actualizacion disponible</p></div>
        </div>
        {compare && (
          <div className="bug-demo-box">
            <strong>Esperado</strong>
            <div className="bug-icon-row"><span>i</span><p>Actualizacion disponible</p></div>
          </div>
        )}
      </div>
      <button type="button" className="bug-demo-action" onClick={() => setCompare((value) => !value)}>{compare ? "Ocultar comparacion" : "Comparar con version correcta"}</button>
    </div>
  );
}

function MicrocopyBugDemo() {
  const [saved, setSaved] = useState(false);

  return (
    <div className="bug-demo-surface">
      <button type="button" className="bug-demo-action" onClick={() => setSaved(true)}>Guardar cambios</button>
      <div className={`bug-demo-status ${saved ? "warning" : ""}`}>{saved ? "Puede que tus cambios se hayan guardado correctamente." : "Pulsa para lanzar la acción"}</div>
    </div>
  );
}

function WorkflowBugDemo() {
  const [step, setStep] = useState(1);
  const initialData = {
    nombre: "Diego",
    apellidos: "Sanchez Martin",
    email: "diego@theqahub.es",
    telefono: "612345678",
    empresa: "TheQAHub",
    rol: "QA Engineer",
    ciudad: "Madrid",
    pais: "Espana",
  };
  const [formData, setFormData] = useState(initialData);
  const [lost, setLost] = useState(false);
  const [snapshot, setSnapshot] = useState(initialData);

  const handleBack = () => {
    if (step > 1) {
      setSnapshot(formData);
      setFormData({
        nombre: "",
        apellidos: "",
        email: "",
        telefono: "",
        empresa: "",
        rol: "",
        ciudad: "",
        pais: "",
      });
      setStep(1);
      setLost(true);
    }
  };

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-progress">
        {["Cuenta", "Perfil", "Preferencias", "Revision"].map((item, index) => (
          <div key={item} className={index + 1 === step ? "current" : index + 1 < step ? "done" : ""}>
            <span>{index + 1}</span>
            <small>{item}</small>
          </div>
        ))}
      </div>
      <div className="bug-demo-grid two">
        <div className="bug-demo-panel">
          <label>Nombre</label>
          <input value={formData.nombre} onChange={(event) => setFormData((value) => ({ ...value, nombre: event.target.value }))} />
          <label>Apellidos</label>
          <input value={formData.apellidos} onChange={(event) => setFormData((value) => ({ ...value, apellidos: event.target.value }))} />
          <label>Email</label>
          <input value={formData.email} onChange={(event) => setFormData((value) => ({ ...value, email: event.target.value }))} />
          <label>Telefono</label>
          <input value={formData.telefono} onChange={(event) => setFormData((value) => ({ ...value, telefono: event.target.value }))} />
          <label>Empresa</label>
          <input value={formData.empresa} onChange={(event) => setFormData((value) => ({ ...value, empresa: event.target.value }))} />
          <label>Rol</label>
          <input value={formData.rol} onChange={(event) => setFormData((value) => ({ ...value, rol: event.target.value }))} />
          <label>Ciudad</label>
          <input value={formData.ciudad} onChange={(event) => setFormData((value) => ({ ...value, ciudad: event.target.value }))} />
          <label>Pais</label>
          <input value={formData.pais} onChange={(event) => setFormData((value) => ({ ...value, pais: event.target.value }))} />
        </div>
        <div className="bug-demo-note">
          <p>{`Estas en el paso ${step} del asistente.`}</p>
          {lost && <p className="bug-loss-alert">Al volver atras se ha perdido el progreso real y hay que empezar desde cero.</p>}
          <strong>Ultimos datos que habias rellenado</strong>
          <p>{`${snapshot.nombre} ${snapshot.apellidos} · ${snapshot.email} · ${snapshot.empresa}`}</p>
          <div className="bug-demo-toolbar">
            <button type="button" onClick={() => { setStep((value) => Math.min(4, value + 1)); if (lost) setLost(false); }}>Avanzar</button>
            <button type="button" onClick={handleBack}>Volver atras</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ValidationBugDemo() {
  const [email, setEmail] = useState("qa@");
  const [phone, setPhone] = useState("123");
  const [status, setStatus] = useState("");

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-panel">
          <label>Email</label>
          <input value={email} onChange={(event) => setEmail(event.target.value)} />
          <label>Telefono</label>
          <input value={phone} onChange={(event) => setPhone(event.target.value)} />
          <button type="button" className="bug-demo-action" onClick={() => setStatus("El frontend lo da por valido y manda la peticion al backend.")}>
            Enviar formulario
          </button>
        </div>
        <div className="bug-demo-box">
          <strong>Payload enviado al backend</strong>
          <pre className="bug-json-preview">{`{\n  "email": "${email}",\n  "phone": "${phone}"\n}`}</pre>
        </div>
      </div>
      <div className={`bug-demo-status ${status ? "error" : ""}`}>{status || "Prueba un email incompleto para ver la validacion rota."}</div>
    </div>
  );
}

function OutOfRangeBugDemo() {
  const [quantity, setQuantity] = useState("-1");

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-panel">
          <label>Cantidad</label>
          <input value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </div>
        <div className="bug-demo-box">
          <strong>Resultado</strong>
          <p>Total calculado: {Number(quantity || 0) * 24} EUR</p>
          <p>Estado: pedido aceptado</p>
        </div>
      </div>
    </div>
  );
}

function BrokenElementDemo() {
  const [state, setState] = useState("idle");

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-panel">
        <button
          type="button"
          className="bug-demo-action ghost"
          onClick={() => {
            setState("loading");
            window.setTimeout(() => setState("done"), 1200);
          }}
        >
          Exportar reporte
        </button>
      </div>
      <div className={`bug-demo-status ${state !== "idle" ? "warning" : ""}`}>
        {state === "idle" && "Pulsa el boton para comprobar que parece funcional, pero no hace su trabajo."}
        {state === "loading" && "Exportando objeto..."}
        {state === "done" && "Objeto exportado correctamente. Pero no aparece ningun archivo ni descarga real."}
      </div>
    </div>
  );
}

function Api500Demo() {
  const [status, setStatus] = useState("idle");

  return (
    <div className="bug-demo-surface">
      <button type="button" className="bug-demo-action" onClick={() => setStatus("error")}>POST /api/orders</button>
      <div className="bug-demo-grid two">
        <div className="bug-demo-box">
          <strong>Payload</strong>
          <pre className="bug-json-preview">{`{\n  "userId": 101,\n  "items": 2,\n  "coupon": "QAHUB10"\n}`}</pre>
        </div>
        <div className={`bug-demo-box ${status === "error" ? "danger" : ""}`}>
          <strong>Respuesta</strong>
          <p>{status === "error" ? "500 Internal Server Error" : "Pendiente"}</p>
          <p>{status === "error" ? "Unexpected null reference in OrderService" : "Sin ejecutar"}</p>
        </div>
      </div>
    </div>
  );
}

function Api400Demo() {
  const [status, setStatus] = useState("idle");

  return (
    <div className="bug-demo-surface">
      <button type="button" className="bug-demo-action" onClick={() => setStatus("error")}>POST /api/profile</button>
      <div className="bug-demo-grid two">
        <div className="bug-demo-box">
          <strong>Payload</strong>
          <pre className="bug-json-preview">{`{\n  "name": "Diego",\n  "country": "ES",\n  "newsletter": true\n}`}</pre>
        </div>
        <div className={`bug-demo-box ${status === "error" ? "danger" : ""}`}>
          <strong>Respuesta</strong>
          <p>{status === "error" ? "400 Bad Request" : "Pendiente"}</p>
          <p>{status === "error" ? "Field 'countryCode' is required" : "Sin ejecutar"}</p>
        </div>
      </div>
    </div>
  );
}

function SignatureGhostDemo() {
  const [environment, setEnvironment] = useState("production");

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-toolbar">
        <button type="button" className={environment === "production" ? "active" : ""} onClick={() => setEnvironment("production")}>
          Produccion
        </button>
        <button type="button" className={environment === "development" ? "active" : ""} onClick={() => setEnvironment("development")}>
          Desarrollo
        </button>
      </div>
      <div className="bug-demo-grid two">
        <div className={`bug-demo-box ${environment === "production" ? "danger" : ""}`}>
          <strong>{environment === "production" ? "Lo que ve el usuario" : "Lo que ve el equipo"}</strong>
          <div className={`bug-mini-card ${environment === "production" ? "broken" : ""}`}>
            <strong>Componente observado</strong>
            <div className={`bug-ghost-panel ${environment === "production" ? "broken" : ""}`}>
              <div className="bug-ghost-header">
                <span className={`bug-ghost-dot ${environment === "production" ? "danger" : "ok"}`} />
                <strong>{environment === "production" ? "Estado inconsistente" : "Estado estable"}</strong>
              </div>
              <div className="bug-ghost-content">
                {environment === "production" ? (
                  <>
                    <div className="bug-ghost-block error">Datos no disponibles</div>
                    <div className="bug-ghost-block faded" />
                    <div className="bug-ghost-block faded short" />
                  </>
                ) : (
                  <>
                    <div className="bug-ghost-block success">Datos cargados correctamente</div>
                    <div className="bug-ghost-block" />
                    <div className="bug-ghost-block short" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bug-demo-box">
          <strong>Diagnostico</strong>
          <p>
            {environment === "production"
              ? "El fallo aparece con trafico real, cookies antiguas y una condicion de cache dificil de aislar."
              : "No hay trazas concluyentes y cada intento de reproduccion devuelve un flujo correcto."}
          </p>
        </div>
      </div>
    </div>
  );
}

function SignatureSuicideDemo() {
  const [triggered, setTriggered] = useState(false);

  return (
    <div className="bug-demo-surface">
      <div className="bug-mini-app">
        <div className="bug-mini-app-header">
          <strong>Panel de usuario</strong>
          <span className={`bug-mini-status ${triggered ? "offline" : "online"}`}>{triggered ? "Sesion caída" : "Sesion activa"}</span>
        </div>
        <div className="bug-mini-app-body">
          <div className={`bug-mini-card ${triggered ? "broken" : ""}`}>
            <p>Usuario autenticado: Diego</p>
            <label className="bug-mini-toggle">
              <span>Newsletter</span>
              <button type="button" onClick={() => setTriggered(true)}>{triggered ? "Off" : "On"}</button>
            </label>
          </div>
          <div className={`bug-mini-grid ${triggered ? "faded" : ""}`}>
            <div className="bug-mini-card">Perfil</div>
            <div className="bug-mini-card">Cursos</div>
            <div className="bug-mini-card">Retos</div>
            <div className="bug-mini-card">Ajustes</div>
          </div>
        </div>
      </div>
      <div className={`bug-demo-status ${triggered ? "error" : ""}`}>
        {triggered ? "Una acción pequena ha roto un area critica completa. Eso es lo que hace que este bug sea suicida." : "Pulsa la acción para ver como un cambio minimo provoca una caída desproporcionada."}
      </div>
    </div>
  );
}

function SignatureWallDemo() {
  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-progress">
        {["Datos", "Direccion", "Pago", "confirmación"].map((item, index) => (
          <div key={item} className={index < 2 ? "done" : index === 2 ? "current" : ""}>
            <span>{index + 1}</span>
            <small>{item}</small>
          </div>
        ))}
      </div>
      <div className="bug-demo-note">
        <p>El usuario ha llegado hasta el paso de pago, pero ya no puede avanzar.</p>
        <button type="button" className="bug-demo-action" disabled>
          Continuar
        </button>
      </div>
      <div className="bug-demo-status error">No hay boton alternativo, no hay salto de paso y no hay workaround. El flujo esta completamente bloqueado.</div>
    </div>
  );
}

function SignatureDominoDemo() {
  const [fixApplied, setFixApplied] = useState(false);

  return (
    <div className="bug-demo-surface">
      <div className="bug-mini-app">
        <div className="bug-mini-app-header">
          <strong>Checkout</strong>
          <button type="button" className="bug-demo-action ghost" onClick={() => setFixApplied(true)}>
            Aplicar fix de precios
          </button>
        </div>
        <div className="bug-mini-app-body">
          <div className="bug-mini-checkout-row">
            <span>Subtotal</span>
            <strong>{fixApplied ? "30€" : "20€ (2 membresías tester pro x 15€)"}</strong>
          </div>
          <div className={`bug-mini-checkout-row ${fixApplied ? "ok-row" : "broken-row"}`}>
            <span>Motor de precios</span>
            <strong>{fixApplied ? "Corregido" : "Calculando mal"}</strong>
          </div>
          <div className={`bug-mini-checkout-row ${fixApplied ? "broken-row" : "ok-row"}`}>
            <span>Cupones</span>
            <strong>{fixApplied ? "Cupón QAHUB10 roto" : "Cupón QAHUB10 aplicado sobre subtotal incorrecto"}</strong>
          </div>
          <div className="bug-mini-checkout-row warning-row">
            <span>Nota del sistema</span>
            <strong>{fixApplied ? "El cupón no se ha aplicado porque el módulo de cupones se ha roto" : "El cupón se aplica, pero sobre un subtotal mal calculado"}</strong>
          </div>
          <div className="bug-mini-checkout-row">
            <span>Total</span>
            <strong>{fixApplied ? "30€" : "10€"}</strong>
          </div>
        </div>
      </div>
      <div className={`bug-demo-status ${fixApplied ? "warning" : ""}`}>
        {fixApplied
          ? "El precio ya sale bien, pero el arreglo ha roto la aplicación del cupón. Eso es el efecto dominó."
          : "Ahora mismo 2 membresías premium de 15€ se están calculando como 20€. Al corregirlo verás que el siguiente paso, el de cupones, deja de funcionar."}
      </div>
    </div>
  );
}

function SignatureSilentDemo() {
  const [step, setStep] = useState(0);
  const samples = [
    { visit: 1, memory: "140 MB", response: "420 ms", memoryAlert: "", responseAlert: "" },
    { visit: 2, memory: "185 MB", response: "690 ms", memoryAlert: "Precaución", responseAlert: "En aumento" },
    { visit: 3, memory: "241 MB", response: "1.1 s", memoryAlert: "Peligro", responseAlert: "Demasiado aumento" },
  ];
  const current = samples[step];

  return (
    <div className="bug-demo-surface">
      <div className="bug-demo-grid two">
        <div className="bug-demo-box">
          <strong>Visita #{current.visit}</strong>
          <p>
            Consumo de memoria: {current.memory}
            {current.memoryAlert ? <span className={`bug-danger-indicator ${current.memoryAlert === "Precaución" ? "warning" : ""}`}>{current.memoryAlert}</span> : null}
          </p>
          <p>
            Tiempo de respuesta: {current.response}
            {current.responseAlert ? <span className={`bug-danger-indicator ${current.responseAlert === "En aumento" ? "warning" : ""}`}>{current.responseAlert}</span> : null}
          </p>
        </div>
        <div className="bug-demo-box">
          <strong>Lectura didactica</strong>
          <p>No explota nada de golpe, pero cada visita consume mas memoria y responde peor que la anterior.</p>
        </div>
      </div>
      <button type="button" className="bug-demo-action" onClick={() => setStep((value) => (value === samples.length - 1 ? 0 : value + 1))}>
        Simular nueva visita
      </button>
    </div>
  );
}

function buildFallbackGuide(bug) {
  return {
    steps: [
      `Abre la demo de ${bug.name}.`,
      "Interactua con el elemento principal del flujo.",
      "Comprueba la diferencia entre lo esperado y lo que realmente ocurre.",
    ],
    expected: bug.report,
    actual: bug.example,
    learn: bug.detect,
    watch: `Como el bug combina ${((bug.categories || [bug.category]).join(" y ")).toLowerCase()} en un mismo caso.`,
  };
}

function renderDemoById(demoId, ui) {
  switch (demoId) {
    case 0:
      return <FunctionalBugDemo ui={ui} />;
    case 1:
      return <UiBugDemo ui={ui} />;
    case 2:
      return <UxBugDemo />;
    case 3:
      return <PerformanceBugDemo />;
    case 4:
      return <SecurityBugDemo />;
    case 5:
      return <CompatibilityBugDemo />;
    case 6:
      return <IntegrationBugDemo />;
    case 7:
      return <LogicBugDemo />;
    case 8:
      return <ConcurrencyBugDemo />;
    case 9:
      return <DataBugDemo />;
    case 10:
      return <VisualMinorBugDemo />;
    case 11:
      return <MicrocopyBugDemo />;
    case 12:
      return <WorkflowBugDemo />;
    case 13:
      return <ValidationBugDemo />;
    case 14:
      return <OutOfRangeBugDemo />;
    case 15:
      return <BrokenElementDemo />;
    case 16:
      return <Api500Demo />;
    case 17:
      return <Api400Demo />;
    default:
      return null;
  }
}

function renderSignatureDemo(name) {
  switch (name) {
    case "Bug fantasma":
    case "Ghost bug":
    case "Geister-Bug":
      return <SignatureGhostDemo />;
    case "Bug suicida":
    case "Suicide bug":
    case "Selbstmord-Bug":
      return <SignatureSuicideDemo />;
    case "Bug muro":
    case "Wall bug":
    case "Mauer-Bug":
      return <SignatureWallDemo />;
    case "Bug efecto dominó":
    case "Domino bug":
    case "Domino-Bug":
      return <SignatureDominoDemo />;
    case "Bug silencioso":
    case "Silent bug":
    case "Leiser Bug":
      return <SignatureSilentDemo />;
    default:
      return null;
  }
}

function BugDemoModal({ bug, demoId, guide, ui, onClose }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <div className="bug-modal-overlay" onClick={onClose}>
      <div className="bug-modal" onClick={(event) => event.stopPropagation()}>
        <div className="bug-modal-header">
          <div>
            <span className={`impact-badge impact-${bug.impact.toLowerCase()}`}>{bug.impact}</span>
            <h2>{bug.name}</h2>
            <p>{bug.description}</p>
          </div>
          <button type="button" className="bug-modal-close" onClick={onClose} aria-label={ui.close}>
            {ui.close}
          </button>
        </div>

        <div className="bug-modal-body">
          <section className="bug-modal-demo">
            <p className="bug-modal-kicker">{ui.liveDemo}</p>
            {renderDemoById(demoId, ui)}
          </section>

          <aside className="bug-modal-sidebar">
            <div className="bug-modal-info-card">
              <h3>{ui.stepsTitle}</h3>
              <ol>
                {guide.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
            <div className="bug-modal-info-card">
              <h3>{ui.expectedTitle}</h3>
              <p>{guide.expected}</p>
            </div>
            <div className="bug-modal-info-card actual">
              <h3>{ui.actualTitle}</h3>
              <p>{guide.actual}</p>
            </div>
            <div className="bug-modal-info-card">
              <h3>{ui.learnTitle}</h3>
              <p>{guide.learn}</p>
              <strong>{ui.watchLabel}</strong>
              <p>{guide.watch}</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function BugDictionary() {
  const { language } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedImpact, setSelectedImpact] = useState("all");
  const [suggestion, setSuggestion] = useState("");
  const [selectedBug, setSelectedBug] = useState(null);
  const [selectedSignature, setSelectedSignature] = useState(null);

  const copy = pageCopy[language] || pageCopy.es;
  const bugs = bugEntries[language] || bugEntries.es;
  const signatureBugs = signatureEntries[language] || signatureEntries.es;
  const ui = modalCopy[language] || modalCopy.es;
  const guides = demoGuides[language] || demoGuides.en;

  const filteredBugs = bugs.map((bug, demoId) => ({ ...bug, demoId })).filter((bug) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const bugCategories = bug.categories || [bug.category];
    const searchMatches =
      !normalizedSearch ||
      [bug.name, bug.category, bugCategories.join(" "), bug.description, bug.example]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);

    const categoryMatches = selectedCategory === "all" || bugCategories.includes(selectedCategory);
    const impactMatches = selectedImpact === "all" || bug.impact === selectedImpact;

    return searchMatches && categoryMatches && impactMatches;
  });

  const mailBody = suggestion.trim() || copy.mailFallback;
  const mailHref = `mailto:diego.sanchez@theqahub.es?subject=${encodeURIComponent(copy.mailSubject)}&body=${encodeURIComponent(mailBody)}`;

  return (
    <div className="bug-dictionary-page">
      <SeoHead
        title="Diccionario de bugs para QA Engineers | TheQAHub"
        description="Diccionario vivo de bugs reales para aprender a clasificar, reportar y detectar incidencias de software con criterio QA."
        canonicalPath="/bugs"
      />
      <section className="bug-hero">
        <div className="bug-hero-copy">
          <p className="bug-kicker">{copy.kicker}</p>
          <h1>{copy.title}</h1>
          <p className="bug-intro">{copy.intro}</p>
        </div>

        <div className="bug-stats-grid">
          {copy.stats.map((item) => (
            <article key={item.label} className="bug-stat-card">
              <strong>{item.value}</strong>
              <span>{renderBrandText(item.label)}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="bug-guide-section">
        <div className="bug-section-heading">
          <p>{copy.guideTitle}</p>
        </div>
        <div className="bug-guide-grid">
          {copy.guide.map((item) => (
            <article key={item.title} className="bug-guide-card">
              <h2>{item.title}</h2>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bug-catalog-section">
        <div className="bug-section-heading">
          <p>{copy.cardsTitle}</p>
          <span>{copy.cardsIntro}</span>
        </div>

        <div className="bug-controls">
          <label className="bug-search">
            <span>{copy.searchLabel}</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={copy.searchPlaceholder}
            />
          </label>

          <div className="bug-filter-group">
            <span>{copy.filterCategory}</span>
            <div className="bug-chip-list">
              <button
                type="button"
                className={selectedCategory === "all" ? "active" : ""}
                onClick={() => setSelectedCategory("all")}
              >
                {copy.allCategories}
              </button>
              {copy.categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={selectedCategory === category ? "active" : ""}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="bug-filter-group">
            <span>{copy.filterImpact}</span>
            <div className="bug-chip-list">
              <button
                type="button"
                className={selectedImpact === "all" ? "active" : ""}
                onClick={() => setSelectedImpact("all")}
              >
                {copy.allImpacts}
              </button>
              {copy.impacts.map((impact) => (
                <button
                  key={impact}
                  type="button"
                  className={selectedImpact === impact ? "active" : ""}
                  onClick={() => setSelectedImpact(impact)}
                >
                  {impact}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filteredBugs.length ? (
          <div className="bug-card-grid">
            {filteredBugs.map((bug) => (
              <article
                key={bug.name}
                className="bug-card bug-card-clickable"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedBug(bug)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedBug(bug);
                  }
                }}
              >
                <div className="bug-card-top">
                  <span className={`impact-badge impact-${bug.impact.toLowerCase()}`}>{bug.impact}</span>
                  <div className="bug-category-list">
                    {(bug.categories || [bug.category]).map((category) => (
                      <span key={`${bug.name}-${category}`} className="bug-category-pill">{category}</span>
                    ))}
                  </div>
                </div>
                <h3>{bug.name}</h3>
                <p>{bug.description}</p>
                <dl>
                  <div>
                    <dt>{copy.fields.origin}</dt>
                    <dd>{bug.origin}</dd>
                  </div>
                  <div>
                    <dt>{copy.fields.detectability}</dt>
                    <dd>{bug.detectability}</dd>
                  </div>
                  <div>
                    <dt>{copy.fields.example}</dt>
                    <dd>{bug.example}</dd>
                  </div>
                  <div>
                    <dt>{copy.fields.report}</dt>
                    <dd>{bug.report}</dd>
                  </div>
                  <div>
                    <dt>{copy.fields.detect}</dt>
                    <dd>{bug.detect}</dd>
                  </div>
                </dl>
                <button type="button" className="bug-card-link" onClick={(event) => { event.stopPropagation(); setSelectedBug(bug); }}>
                  {ui.openDemo}
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="bug-empty-state">
            <h3>{copy.emptyTitle}</h3>
            <p>{copy.emptyText}</p>
          </div>
        )}
      </section>

      <section className="bug-signature-section">
        <div className="bug-section-heading">
          <p>
            {copy.signatureEyebrow} <span className="theqahub-font">{"</"}TheQAHub{">"}</span>
          </p>
          <span>{renderBrandText(copy.signatureIntro)}</span>
        </div>
        <div className="bug-signature-grid">
          {signatureBugs.map((item) => (
            <article key={item.name} className="bug-signature-card">
              <h3>{item.name}</h3>
              <p>{item.text}</p>
              <button type="button" className="bug-card-link" onClick={() => setSelectedSignature(item)}>
                {ui.openDemo}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="bug-expand-section">
        <div className="bug-expand-copy">
          <p>{copy.expandTitle}</p>
          <span>{renderBrandText(copy.expandText)}</span>
          <small>{copy.mailNote}</small>
        </div>

        <div className="bug-suggestion-card">
          <label htmlFor="bug-suggestion">{copy.suggestionLabel}</label>
          <textarea
            id="bug-suggestion"
            value={suggestion}
            onChange={(event) => setSuggestion(event.target.value)}
            placeholder={copy.suggestionPlaceholder}
          />

          <div className="bug-suggestion-actions">
            <a href={mailHref} className="bug-primary-action">
              {copy.sendMail}
            </a>
            <Link to="/contacto" className="bug-secondary-action">
              {copy.contactCta}
            </Link>
          </div>
        </div>
      </section>

      {selectedBug && (
        <BugDemoModal
          bug={selectedBug}
          demoId={selectedBug.demoId}
          guide={guides[selectedBug.demoId] || buildFallbackGuide(selectedBug)}
          ui={ui}
          onClose={() => setSelectedBug(null)}
        />
      )}

      {selectedSignature && (
        <div className="bug-modal-overlay" onClick={() => setSelectedSignature(null)}>
          <div className="bug-modal bug-signature-modal" onClick={(event) => event.stopPropagation()}>
            <div className="bug-modal-header">
              <div>
                <span className="bug-category-pill">{renderBrandText("Firma TheQAHub")}</span>
                <h2>{selectedSignature.name}</h2>
                <p>{selectedSignature.text}</p>
              </div>
              <button type="button" className="bug-modal-close" onClick={() => setSelectedSignature(null)} aria-label={ui.close}>
                {ui.close}
              </button>
            </div>
            <div className="bug-modal-body">
              <section className="bug-modal-demo">
                <p className="bug-modal-kicker">Contexto de marca</p>
                {renderSignatureDemo(selectedSignature.name)}
              </section>
              <aside className="bug-modal-sidebar">
                <div className="bug-modal-info-card">
                  <h3>Como se usa</h3>
                  <p>{selectedSignature.usage}</p>
                </div>
                <div className="bug-modal-info-card actual">
                  <h3>Escenario ejemplo</h3>
                  <p>{selectedSignature.example}</p>
                </div>
                <div className="bug-modal-info-card">
                  <h3>{renderBrandText("Por que nos interesa en TheQAHub")}</h3>
                  <p>Este tipo de firma nos ayuda a comunicar mejor, enseñar mejor y convertir patrones difusos en lenguaje compartido de equipo.</p>
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BugDictionary;
